import { useEffect, useRef, useState } from 'react';
import { X, Search, LoaderCircle } from 'lucide-react';
import { consultarPadronAFIP, createCliente, getClienteById, searchClients } from '@/services/clients.js';
import { mapAfipResponsabilidadIva } from '@/services/clientFiscal.js';
import { useFocusTrap } from '@/hooks/useKeyboardShortcuts.js';
import ProvinciaSelect from '@/components/geo/ProvinciaSelect.jsx';
import LocalidadSelect from '@/components/geo/LocalidadSelect.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import DniCaptureField from './DniCaptureField.jsx';
import { findClienteByDocument } from '@/services/clients.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const INITIAL_CREATE = {
  razonSocial: '',
  tipoDocumento: 'CUIT',
  cuit: '',
  condicionIVA: 'CF',
  domicilio: '',
  cp: '',
  provinciaId: '',
  localidadId: '',
  telefono: '',
  contacto: '',
};

const IVA_OPTIONS = [
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributo' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'EX', label: 'Exento' },
  { value: 'RNI', label: 'Responsable no inscripto' },
];

const onlyDigits = value => String(value || '').replace(/\D/g, '');

const formatCuit = value => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

const isValidCuit = value => {
  const digits = onlyDigits(value);
  if (digits.length !== 11) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const total = weights.reduce((sum, weight, index) => sum + Number(digits[index]) * weight, 0);
  let verifier = 11 - (total % 11);
  if (verifier === 11) verifier = 0;
  if (verifier === 10) verifier = 9;
  return verifier === Number(digits[10]);
};

const getAfipAddress = afip => {
  const addresses = afip?.persona_raw?.domicilio || [];
  const address = addresses.find(item => item.tipoDomicilio === 'FISCAL')
    || addresses.find(item => item.tipoDomicilio === 'LEGAL/REAL')
    || addresses[0];
  return {
    direccion: [address?.calle, address?.numero].filter(Boolean).join(' ') || address?.direccion || afip?.direccion || '',
    cp: address?.codigoPostal || afip?.codigoPostal || '',
  };
};

/** Modal reusable to find or create a client without leaving the current flow. */
export default function CustomerModal({ isOpen, onClose, onSelect, initialTab = 'buscar', eventualMode = false, initialCapture = false, excludeId = null }) {
  const { tenant, can } = useAuth();
  const tenantKey = tenant?.id || tenant?.code || tenant;
  const canCreateEventual = can('CLIENTES_CREAR') && can('CLIENTES_VER');
  const [capture, setCapture] = useState(initialCapture);
  const [documentMatch, setDocumentMatch] = useState(null);
  const [checkingDocument, setCheckingDocument] = useState(false);
  const request = useRef(0);
  const live = useRef({ isOpen, tenantKey });
  live.current = { isOpen, tenantKey };
  const previousTenant = useRef(tenantKey);
  const invalidateDocument = () => {
    request.current += 1;
    setDocumentMatch(null);
    setCheckingDocument(false);
  };
  const close = () => {
    invalidateDocument();
    setCapture(false);
    onClose();
  };
  useEffect(() => {
    if (previousTenant.current !== tenantKey) {
      previousTenant.current = tenantKey;
      invalidateDocument();
      setCreateForm({ ...INITIAL_CREATE, tipoDocumento: eventualMode ? 'DNI' : 'CUIT' });
      setCapture(false);
      if (isOpen && eventualMode) onClose();
    }
    return () => { request.current += 1; };
  }, [tenantKey]);
  useEffect(() => {
    invalidateDocument();
    setIsSubmitting(false);
    setCapture(isOpen && eventualMode && initialCapture);
    if (eventualMode) setCreateForm({ ...INITIAL_CREATE, tipoDocumento: 'DNI' });
    return () => { request.current += 1; };
  }, [isOpen, eventualMode, initialCapture]);

  // Requests belong to one opening, tenant and final document revision.
  const lookupDocument = async (form, ticket, scopeTenant) => {
    setCheckingDocument(true);
    try {
      const match = await findClienteByDocument({ tipoDocumento: form.tipoDocumento, numeroDocumento: onlyDigits(form.cuit) });
      if (request.current !== ticket || !live.current.isOpen || live.current.tenantKey !== scopeTenant) return { stale: true };
      setDocumentMatch(match);
      if (match) setCreateMessage(String(match.id) === String(excludeId) ? 'El cliente corresponde a la otra parte de la guía.' : 'El documento ya existe. Revisá el registro antes de seleccionarlo.');
      else setCreateMessage(canCreateEventual
        ? 'No se encontró un cliente con este documento. Revisá los datos para crear y seleccionar.'
        : 'No se encontró un cliente con este documento. No tenés permiso para crear clientes. Podés volver a escanear o cancelar.');
      return { match };
    } catch {
      if (request.current !== ticket || !live.current.isOpen || live.current.tenantKey !== scopeTenant) return { stale: true };
      setCreateMessage('No se pudo comprobar el documento. Podés corregir los datos manualmente y volver a intentar; no se creará sin comprobar duplicados.');
      return { failed: true };
    } finally {
      if (request.current === ticket) setCheckingDocument(false);
    }
  };
  const handleParsed = data => {
    const form = { ...createForm, razonSocial: `${data.lastName} ${data.firstName}`, tipoDocumento: 'DNI', cuit: data.documentNumber };
    setCreateForm(form);
    setCreateErrors({});
    setVerifiedCuit('');
    setCreateMessage('');
    const ticket = ++request.current;
    void lookupDocument(form, ticket, tenantKey);
  };
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState({ q: '', cuit: '', provinciaId: '', provinciaNombre: '', localidadId: '', localidadNombre: '' });
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [createForm, setCreateForm] = useState(() => ({ ...INITIAL_CREATE, tipoDocumento: eventualMode ? 'DNI' : 'CUIT', email: '' }));
  const [createErrors, setCreateErrors] = useState({});
  const [createMessage, setCreateMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingAfip, setIsCheckingAfip] = useState(false);
  const [verifiedCuit, setVerifiedCuit] = useState('');
  const firstFieldRef = useRef(null);

  useFocusTrap(isOpen);

  useEffect(() => {
    if (isOpen && !capture) {
      const timer = setTimeout(() => firstFieldRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, activeTab, capture]);

  useEffect(() => {
    if (isOpen) return;
    setActiveTab(initialTab);
    setFilters({ q: '', cuit: '', provinciaId: '', provinciaNombre: '', localidadId: '', localidadNombre: '' });
    setSearchResults([]);
    setSearchError('');
    setCreateForm({ ...INITIAL_CREATE, tipoDocumento: eventualMode ? 'DNI' : 'CUIT', email: '' });
    setCreateErrors({});
    setCreateMessage('');
    setVerifiedCuit('');
  }, [isOpen, initialTab]);

  const handleSearch = async () => {
    setSearching(true);
    setSearchError('');
    try {
      const response = await searchClients({
        // The API global search already covers both name and document.
        search: filters.q.trim() || filters.cuit.trim() || undefined,
        provincia: filters.provinciaNombre || undefined,
        localidad: filters.localidadNombre || undefined,
        pageSize: 50,
      });
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error buscando clientes', error);
      setSearchResults([]);
      setSearchError(error.message || 'No se pudo realizar la búsqueda.');
    } finally {
      setSearching(false);
    }
  };

  const updateCreate = (field, value) => {
    setCreateForm(previous => ({ ...previous, [field]: value }));
    setCreateErrors(previous => ({ ...previous, [field]: '' }));
    if (field === 'cuit' || field === 'tipoDocumento') {
      setVerifiedCuit('');
      if (eventualMode) { invalidateDocument(); setCreateMessage(''); }
    }
  };

  const handleDocumentTypeChange = event => {
    const tipoDocumento = event.target.value;
    if (eventualMode) { invalidateDocument(); setCreateMessage(''); }
    setCreateForm(previous => ({ ...previous, tipoDocumento, cuit: '' }));
    setVerifiedCuit('');
    setCreateErrors(previous => ({ ...previous, cuit: '' }));
  };

  const handleCheckAfip = async () => {
    const cuit = onlyDigits(createForm.cuit);
    if (!isValidCuit(cuit)) {
      setCreateErrors(previous => ({ ...previous, cuit: 'Ingresá un CUIT válido (XX-XXXXXXXX-X).' }));
      return;
    }
    setIsCheckingAfip(true);
    setCreateMessage('');
    try {
      const afip = await consultarPadronAFIP(cuit, true);
      if (!afip?.ok) throw new Error(afip?.msg || 'AFIP no devolvió datos para este CUIT.');
      if (String(afip.estadoContribuyente || '').toUpperCase() === 'INACTIVO') {
        throw new Error('El contribuyente figura INACTIVO en AFIP y no puede ser creado.');
      }
      const address = getAfipAddress(afip);
      setCreateForm(previous => ({
        ...previous,
        cuit: formatCuit(afip.cuit || cuit),
        razonSocial: afip.razonSocial || previous.razonSocial,
        condicionIVA: mapAfipResponsabilidadIva(afip.condicionIVA, { format: 'code', fallback: previous.condicionIVA }) || previous.condicionIVA,
        domicilio: address.direccion || previous.domicilio,
        cp: address.cp || previous.cp,
      }));
      setVerifiedCuit(onlyDigits(afip.cuit || cuit));
      setCreateMessage('CUIT verificado y datos completados desde AFIP.');
    } catch (error) {
      setVerifiedCuit('');
      setCreateMessage(error.message || 'No se pudo consultar AFIP.');
    } finally {
      setIsCheckingAfip(false);
    }
  };

  const validateCreate = () => {
    const errors = {};
    const document = onlyDigits(createForm.cuit);
    if (!createForm.razonSocial.trim()) errors.razonSocial = 'Ingresá la razón social.';
    if (createForm.tipoDocumento === 'CUIT') {
      if (!isValidCuit(document)) errors.cuit = 'Ingresá un CUIT válido (XX-XXXXXXXX-X).';
      else if (verifiedCuit !== document) errors.cuit = 'Verificá este CUIT con AFIP antes de crear el cliente.';
    } else if (!/^\d{7,8}$/.test(document)) {
      errors.cuit = 'El DNI debe tener 7 u 8 dígitos, sin puntos ni letras.';
    }
    if (!createForm.condicionIVA) errors.condicionIVA = 'Seleccioná la condición frente al IVA.';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async event => {
    event.preventDefault();
    if (eventualMode) event.stopPropagation();
    if (isSubmitting || (eventualMode && !canCreateEventual) || !validateCreate()) return;
    const ticket = ++request.current;
    const scopeTenant = tenantKey;
    const stillCurrent = () => request.current === ticket && live.current.isOpen && live.current.tenantKey === scopeTenant;
    setIsSubmitting(true);
    setCreateMessage('');
    try {
      if (eventualMode) {
        const result = await lookupDocument(createForm, ticket, scopeTenant);
        if (result.stale || result.failed || result.match) return;
      }
      const payload = {
        razonSocial: createForm.razonSocial.trim(),
        tipoDocumento: createForm.tipoDocumento,
        cuit: createForm.tipoDocumento === 'CUIT' ? formatCuit(createForm.cuit) : onlyDigits(createForm.cuit),
        condicionIVA: createForm.condicionIVA,
        categoriaCliente: 'Minorista',
        direccion: createForm.domicilio.trim(),
        codigoPostal: createForm.cp.trim(),
        provinciaId: createForm.provinciaId || null,
        localidadId: createForm.localidadId || null,
        telefono: createForm.telefono.trim(),
        contacto: createForm.contacto.trim(),
      };
      if (eventualMode) {
        payload.tipoCliente = 'eventual';
        payload.validarDocumentoExacto = true;
        if (createForm.email?.trim()) payload.emails = [createForm.email.trim()];
      }
      const response = await createCliente(payload);
      if (eventualMode && !stillCurrent()) return;
      if (eventualMode && response?.code === 'CONFLICT') {
        const result = await lookupDocument(createForm, ticket, scopeTenant);
        if (!result.stale && !result.failed && !result.match) setCreateMessage('El documento entró en conflicto. Revisá los datos y volvé a intentar.');
        return;
      }
      if (eventualMode && response?.code === 'DOCUMENT_UNIQUENESS_NOT_READY') {
        setCreateMessage('El alta por documento no está habilitada: falta verificar la unicidad en la base de datos. Contacte al administrador.');
        return;
      }
      if (!response?.success) throw new Error(response?.message || 'No se pudo crear el cliente.');
      const client = await getClienteById(response.id);
      if (eventualMode && !stillCurrent()) return;
      onSelect?.(client || { ...payload, id: response.id });
      close();
    } catch (error) {
      if (!eventualMode) console.error('Error creando cliente', error);
      if (!eventualMode || stillCurrent()) setCreateMessage(eventualMode ? 'No se pudo crear el cliente. Revisá los datos e intentá nuevamente.' : error.message || 'No se pudo crear el cliente.');
    } finally {
      if (!eventualMode || stillCurrent()) setIsSubmitting(false);
    }
  };

  const AdditionalFields = eventualMode ? 'details' : 'div';
  return (
    <ModalBase isOpen={isOpen} onClose={close} labelledBy="customer-modal-title" className={`modal modal-content w-full ${eventualMode ? 'max-w-[940px] flex flex-col' : 'max-w-5xl'} overflow-hidden p-0 focus-visible:outline-none max-h-[90vh]`}>
      <div className="flex items-center justify-between border-b border-[rgb(var(--border))] px-6 py-4">
        <div><h2 id="customer-modal-title" className="text-lg font-semibold text-[rgb(var(--fg))]">{eventualMode ? 'Alta rápida de cliente' : 'Buscar o crear cliente'}</h2><p className="text-sm text-[rgb(var(--muted))]">{eventualMode ? 'EVENTUAL · MOSTRADOR / Complete, revise y seleccione sin salir de la guía.' : 'Seleccioná un cliente existente o creá uno nuevo sin salir del flujo.'}</p></div>
        <button type="button" onClick={close} className="rounded-xl p-2 text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),.2)]" aria-label="Cerrar modal"><X className="h-5 w-5" /></button>
      </div>
      {!eventualMode && <div className="flex gap-2 border-b border-[rgb(var(--border))] px-6 py-3">
        {['buscar', 'crear'].map(tab => <button key={tab} type="button" onClick={() => setActiveTab(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === tab ? 'bg-[rgba(var(--brand),.15)] text-[rgb(var(--fg))]' : 'text-[rgb(var(--muted))]'}`}>{tab === 'buscar' ? 'Buscar' : 'Crear'}</button>)}
      </div>}
      {!eventualMode && activeTab === 'buscar' ? <div className="space-y-4 px-6 py-6">
        <div className="grid grid-cols-12 gap-4">
          <label className="col-span-12 md:col-span-6"><span className="mb-1 block text-sm font-medium">Búsqueda general</span><div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" /><input ref={firstFieldRef} value={filters.q} onChange={event => setFilters(previous => ({ ...previous, q: event.target.value }))} onKeyDown={event => event.key === 'Enter' && handleSearch()} placeholder="Razón social, contacto, dirección..." className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-9 py-2.5 text-sm" /></div></label>
          <label className="col-span-12 md:col-span-3"><span className="mb-1 block text-sm font-medium">CUIT / DNI</span><input value={filters.cuit} onChange={event => setFilters(previous => ({ ...previous, cuit: event.target.value }))} onKeyDown={event => event.key === 'Enter' && handleSearch()} placeholder="30-12345678-9" className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" /></label>
          <div className="col-span-12 md:col-span-3"><button type="button" onClick={handleSearch} disabled={searching} className="mt-6 w-full rounded-xl bg-[rgb(var(--brand))] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{searching ? 'Buscando...' : 'Buscar'}</button></div>
          <div className="col-span-12 md:col-span-6"><ProvinciaSelect label="Provincia" value={filters.provinciaId} onChange={value => setFilters(previous => ({ ...previous, provinciaId: value, localidadId: '', localidadNombre: '' }))} onOptionSelect={option => setFilters(previous => ({ ...previous, provinciaNombre: option?.nombre || '' }))} /></div>
          <div className="col-span-12 md:col-span-6"><LocalidadSelect label="Localidad" provinciaId={filters.provinciaId} value={filters.localidadId} onChange={value => setFilters(previous => ({ ...previous, localidadId: value }))} onOptionSelect={option => setFilters(previous => ({ ...previous, localidadNombre: option?.nombre || '' }))} /></div>
        </div>
        {searchError ? <p className="text-sm text-rose-600">{searchError}</p> : null}
        <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))]"><table className="w-full text-sm"><thead className="bg-[rgba(var(--subpanel))] text-[rgb(var(--muted))]"><tr><th className="px-4 py-3 text-left">Razón social</th><th className="px-4 py-3 text-left">CUIT / DNI</th><th className="px-4 py-3 text-left">Localidad</th><th className="px-4 py-3 text-left">Teléfono</th></tr></thead><tbody className="divide-y divide-[rgb(var(--border))]">{!searching && searchResults.length === 0 ? <tr><td className="px-4 py-4 text-[rgb(var(--muted))]" colSpan={4}>Sin resultados. Ajustá los filtros y volvé a buscar.</td></tr> : null}{searchResults.map(customer => <tr key={customer.id} className="cursor-pointer hover:bg-[rgba(var(--muted),.12)]" onClick={() => { onSelect?.(customer); onClose(); }}><td className="px-4 py-3">{customer.razonSocial}</td><td className="px-4 py-3">{customer.cuit || '—'}</td><td className="px-4 py-3">{customer.localidad?.nombre || '—'}</td><td className="px-4 py-3">{customer.telefono || '—'}</td></tr>)}</tbody></table></div>
      </div> : <form onSubmit={handleCreate} className={`min-h-0 overflow-y-auto px-6 py-6 ${eventualMode ? `dni-eventual-form${capture ? ' dni-eventual-form--capture' : ''}` : 'space-y-4'}`}>
        {eventualMode && <div className="space-y-3">
          {capture ? <DniCaptureField onAttempt={() => { invalidateDocument(); setCreateMessage(''); }} onParsed={handleParsed} disabled={isSubmitting} /> : <button type="button" onClick={() => setCapture(true)} className="rounded border px-3 py-2">Escanear DNI</button>}
          {!canCreateEventual && <p role="alert">Podés seleccionar un cliente existente con permiso de consulta. Para crear uno nuevo se requieren permisos de consulta y creación.</p>}
          {checkingDocument && <p role="status">Comprobando documento...</p>}
          {documentMatch && <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm" aria-label="Cliente existente">
            <p>{documentMatch.razonSocial}</p><p>{documentMatch.cuit} · {documentMatch.telefono}</p>
            {String(documentMatch.id) !== String(excludeId) && <button type="button" disabled={isSubmitting || checkingDocument} className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white" onClick={() => { onSelect?.(documentMatch); close(); }}>Usar cliente existente</button>}
          </section>}
        </div>}
        <fieldset disabled={isSubmitting} className="grid grid-cols-12 gap-4">
          <label className="col-span-12 md:col-span-6"><span className="mb-1 block text-sm font-medium">Razón social *</span><input ref={firstFieldRef} value={createForm.razonSocial} onChange={event => updateCreate('razonSocial', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" />{createErrors.razonSocial ? <p className="mt-1 text-xs text-rose-600">{createErrors.razonSocial}</p> : null}</label>
          <label className="col-span-12 md:col-span-3"><span className="mb-1 block text-sm font-medium">Tipo de documento *</span><select value={createForm.tipoDocumento} onChange={handleDocumentTypeChange} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm"><option value="CUIT">CUIT</option><option value="DNI">DNI</option></select></label>
          <label className="col-span-12 md:col-span-3"><span className="mb-1 block text-sm font-medium">{createForm.tipoDocumento} *</span><input value={createForm.cuit} onChange={event => updateCreate('cuit', createForm.tipoDocumento === 'CUIT' ? formatCuit(event.target.value) : onlyDigits(event.target.value).slice(0, 8))} placeholder={createForm.tipoDocumento === 'CUIT' ? '30-12345678-9' : '12345678'} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" />{createErrors.cuit ? <p className="mt-1 text-xs text-rose-600">{createErrors.cuit}</p> : null}</label>
          {createForm.tipoDocumento === 'CUIT' ? <div className="col-span-12 flex items-end gap-3"><button type="button" onClick={handleCheckAfip} disabled={isCheckingAfip} className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--brand))] px-4 py-2.5 text-sm font-semibold text-[rgb(var(--brand))] disabled:opacity-60">{isCheckingAfip ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}{isCheckingAfip ? 'Consultando AFIP...' : verifiedCuit ? 'Verificado por AFIP' : 'Verificar AFIP'}</button><span className="text-xs text-[rgb(var(--muted))]">La verificación AFIP es obligatoria para CUIT.</span></div> : <p className="col-span-12 text-xs text-[rgb(var(--muted))]">Para DNI se valida el formato de 7 u 8 dígitos.</p>}
          <AdditionalFields className="col-span-12">{eventualMode && <summary>Datos adicionales: domicilio, localidad y fiscales</summary>}<div className="grid grid-cols-12 gap-4">
          <label className="col-span-12 md:col-span-4"><span className="mb-1 block text-sm font-medium">Condición frente al IVA *</span><select value={createForm.condicionIVA} onChange={event => updateCreate('condicionIVA', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm">{IVA_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>{createErrors.condicionIVA ? <p className="mt-1 text-xs text-rose-600">{createErrors.condicionIVA}</p> : null}</label>
          <label className="col-span-12 md:col-span-5"><span className="mb-1 block text-sm font-medium">Domicilio</span><input value={createForm.domicilio} onChange={event => updateCreate('domicilio', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" /></label>
          <label className="col-span-12 md:col-span-3"><span className="mb-1 block text-sm font-medium">Código postal</span><input value={createForm.cp} onChange={event => updateCreate('cp', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" /></label>
          <div className="col-span-12 md:col-span-6"><ProvinciaSelect label="Provincia" value={createForm.provinciaId} onChange={value => setCreateForm(previous => ({ ...previous, provinciaId: value, localidadId: '' }))} /></div>
          <div className="col-span-12 md:col-span-6"><LocalidadSelect label="Localidad" provinciaId={createForm.provinciaId} value={createForm.localidadId} onChange={value => updateCreate('localidadId', value)} /></div>
          </div></AdditionalFields>
          {eventualMode && <label className="col-span-12 md:col-span-6"><span className="mb-1 block text-sm font-medium">Email (opcional)</span><input type="email" value={createForm.email || ''} onChange={event => updateCreate('email', event.target.value)} className="w-full rounded-xl border px-4 py-2.5 text-sm" /></label>}
          <label className="col-span-12 md:col-span-6"><span className="mb-1 block text-sm font-medium">Teléfono</span><input value={createForm.telefono} onChange={event => updateCreate('telefono', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" /></label>
          <label className="col-span-12 md:col-span-6"><span className="mb-1 block text-sm font-medium">Contacto</span><input value={createForm.contacto} onChange={event => updateCreate('contacto', event.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-4 py-2.5 text-sm" /></label>
        </fieldset>
        {createMessage ? <p className={`dni-form-message text-sm ${verifiedCuit ? 'text-emerald-600' : 'text-rose-600'}`}>{createMessage}</p> : null}
        <div className={`${eventualMode ? 'dni-form-actions' : ''} flex justify-end gap-3`}><button type="button" onClick={close} disabled={isSubmitting} className="rounded-xl border border-[rgb(var(--border))] px-4 py-2.5 text-sm font-semibold">Cancelar</button><button type="submit" disabled={isSubmitting || (eventualMode && (!canCreateEventual || checkingDocument))} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{isSubmitting ? 'Guardando...' : eventualMode ? 'Crear y seleccionar' : 'Crear cliente'}</button></div>
      </form>}
    </ModalBase>
  );
}
